import axios from "axios";
import { getCurrentSessionIdToken } from "../queries/AuthToken";
import { Answer } from "../types/answer";
import { ReviewAnswer, ReviewAnswerUpdate } from "../types/reviewAnswer";
import { QuestionType } from "../types/checklist";
import { errorToast, successToast } from "@idwal/idwal-react-components";

const getInspectionApiHeaders = async () => {
    const token = await getCurrentSessionIdToken();
    return {
        "Content-Type": "application/json",
        "Authorization": 'Bearer ' + token
    };
};

/**
 * The surveyor's original submission, from the answers table. This
 * is the untouched as-synced data — tech-review edits live in the
 * inspection table which is handled by getReviewAnswers
 */
export const getSurveyorAnswers = async (inspectionId: string): Promise<Answer[] | undefined> => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections/${inspectionId}`;

    const headers = await getInspectionApiHeaders();

    try {
        const response = await axios.get(url, { headers });

        if (response.data?.answers) {
            // Transform object keyed by questionId to array
            const answersObj = response.data.answers;
            return Object.entries(answersObj).map(([questionId, answerData]): Answer => {
                const data = answerData as Record<string, unknown>;
                return {
                    answerId: questionId,
                    inspectionId,
                    questionId,
                    questionText: data.questionText as string | null,
                    value: data.value,
                    dataDictionaryId: data.dataDictionaryId as string | null,
                    createdAt: data.timestamp as string | null,
                    updatedAt: data.syncedAt as string | null,
                    questionType: data.questionType as QuestionType | null | undefined,
                    isNotable: data.isNotable as boolean | undefined,
                    inspectionNumber: data.inspectionNumber,
                };
            });
        }
    } catch {
        errorToast(`Failed to get answers for inspection: ${inspectionId}`);
    }
};

export const getInspectionRecord = async (inspectionId: string) => {
    // includeAnswers=false: we only want the inspection record here (vessel,
    // checklistType/version, status). The review answers come from the separate
    // review-answers endpoint.
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections/${inspectionId}?includeAnswers=false`;

    const headers = await getInspectionApiHeaders();

    try {
        const response = await axios.get(url, { headers });

        if (response.data?.inspection) {
            const inspection = response.data.inspection;
            // Mirror getInspectionsList: expose inspectionId as `id` too. Keep the
            // asset-manager `images` block (photos grouped by question) — fall back
            // to the top level in case the backend returns it as a sibling.
            return { ...inspection, id: inspectionId, images: inspection.images ?? response.data.images };
        }
    } catch {
        errorToast(`Failed to get inspection: ${inspectionId}`);
    }
};

export const getReviewAnswers = async (inspectionId: string): Promise<ReviewAnswer[] | undefined> => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections/${inspectionId}/review-answers`;

    const headers = await getInspectionApiHeaders();

    try {
        const response = await axios.get(url, { headers });

        if (response.data?.answers) {
            return response.data.answers as ReviewAnswer[];
        }
    } catch {
        errorToast(`Failed to get review answers for inspection: ${inspectionId}`);
    }
};

/**
 * Update a reviewed answer in the inspection table. `clears` carries the
 * questionNames of descendant answers orphaned by this edit; the backend
 * hard-deletes them in the same transaction. For a multi-choice question,
 * `optionIds` is its full option-id set so the backend can delete the deselected
 * option rows by exact key.
 */
export const updateReviewAnswer = async (
    inspectionId: string,
    update: ReviewAnswerUpdate,
    clears: string[] = [],
    optionIds?: string[]
): Promise<boolean> => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections/${inspectionId}/review-answers`;

    const headers = await getInspectionApiHeaders();

    try {
        const body = optionIds ? { ...update, clears, optionIds } : { ...update, clears };
        const response = await axios.post(url, body, { headers });
        return response.status === 200;
    } catch {
        errorToast("Failed to update response");
        return false;
    }
};

/**
 * Manually (re)trigger the EEXI/CII calculators for an inspection — they
 * re-read the reviewed answers, so a reviewer can re-run after edits.
 * Reviewer/admin only; the backend rejects other callers.
 */
export const triggerEmissionsCalculation = async (inspectionId: string): Promise<boolean> => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections/${inspectionId}/calculate-emissions`;

    const headers = await getInspectionApiHeaders();

    try {
        const response = await axios.post(url, {}, { headers });
        if (response.status === 200) {
            successToast("EEXI/CII calculation triggered");
            return true;
        }
        errorToast("Failed to trigger the EEXI/CII calculation");
        return false;
    } catch {
        errorToast("Failed to trigger the EEXI/CII calculation");
        return false;
    }
};

export const getInspectionsList = async () => {
    const url = process.env.REACT_APP_INSPECTION_API + `/inspections`;

    const headers = await getInspectionApiHeaders();

    try {
        const response = await axios.get(url, { headers });

        if (response.data?.inspections) {
            // Map inspectionId to id for compatibility with existing Inspection type
            return response.data.inspections.map((inspection: Record<string, unknown>) => ({
                ...inspection,
                id: inspection.inspectionId,
            }));
        }
    } catch {
        errorToast("Failed to get inspections list");
    }
};
